/**
 * Cognito Hosted UI OAuth (authorization-code) flow, implemented with plain
 * fetch — no aws-amplify. AppSync uses Cognito user-pool auth, so API calls
 * carry the user's id token.
 *
 * Flow:
 *  - ensureSignedIn(): if a valid token is cached, done. If the URL has an
 *    auth `code`, exchange it for tokens. Otherwise redirect to the Hosted UI.
 *  - getIdToken(): returns the cached id token (or null).
 *
 * Tokens are kept in sessionStorage. The token endpoint is the Cognito domain
 * from runtime settings; the redirect URI is the app origin.
 */
import { RuntimeSettings } from "./runtimeConfig";

const ID_TOKEN_KEY = "auto_wbr_id_token";
const EXP_KEY = "auto_wbr_id_token_exp";

function redirectUri(): string {
  return window.location.origin + "/";
}

function isExpired(): boolean {
  const exp = Number(sessionStorage.getItem(EXP_KEY) || "0");
  // refresh a minute early
  return Date.now() >= exp - 60_000;
}

export function getIdToken(): string | null {
  const t = sessionStorage.getItem(ID_TOKEN_KEY);
  if (!t || isExpired()) return null;
  return t;
}

function storeTokens(idToken: string, expiresInSec: number) {
  sessionStorage.setItem(ID_TOKEN_KEY, idToken);
  sessionStorage.setItem(EXP_KEY, String(Date.now() + expiresInSec * 1000));
}

function authorizeUrl(s: RuntimeSettings): string {
  const params = new URLSearchParams({
    client_id: s.userPoolClientId,
    response_type: "code",
    scope: "openid",
    redirect_uri: redirectUri(),
  });
  return `https://${s.cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

async function exchangeCodeForTokens(
  s: RuntimeSettings,
  code: string
): Promise<boolean> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: s.userPoolClientId,
    code,
    redirect_uri: redirectUri(),
  });
  const res = await fetch(`https://${s.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) return false;
  const json = await res.json();
  if (!json.id_token) return false;
  storeTokens(json.id_token, json.expires_in || 3600);
  return true;
}

/**
 * Ensure there is a valid session. Returns true if signed in. May redirect the
 * browser to the Hosted UI (in which case the promise never resolves).
 */
export async function ensureSignedIn(s: RuntimeSettings): Promise<boolean> {
  if (getIdToken()) return true;

  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  if (code) {
    const ok = await exchangeCodeForTokens(s, code);
    // strip the ?code= from the address bar regardless of outcome
    url.searchParams.delete("code");
    window.history.replaceState({}, document.title, url.pathname + url.search);
    if (ok) return true;
  }

  // No token, no code → kick off the hosted-UI login.
  window.location.assign(authorizeUrl(s));
  return false;
}

export function currentUsername(): string {
  const t = getIdToken();
  if (!t) return "you";
  try {
    const payload = JSON.parse(atob(t.split(".")[1]));
    return payload["cognito:username"] || payload.email || payload.sub || "you";
  } catch {
    return "you";
  }
}
